import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { PrepaymentUserBalanceBlockConfig } from '@front-platform/content-builder';
import { prepaymentUserBalanceBlockResolveConfig } from '@front-platform/content-builder';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input } from '@angular/core';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { PrepaymentSelectorService } from '@common/selectors/prepayment.selector';
import { PrepaymentStoreDispatcher } from '@common/dispatchers/prepayment.dispatcher';
import { toObservable } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs';
import { ShopPickerEntities } from '@core/models/shop.model';
import { PriceAtomComponent } from '@shared/atoms/price-atom/price-atom.component';
export interface PrepaymentUserBalanceBlockRuntimeConfig {
  'shop_entity'?: ShopPickerEntities;
}

export type PrepaymentUserBalanceBlockConfigInput = Partial<
  Omit<PrepaymentUserBalanceBlockConfig, keyof PrepaymentUserBalanceBlockRuntimeConfig> &
    PrepaymentUserBalanceBlockRuntimeConfig
>;
export type PrepaymentUserBalanceBlockResolvedConfig = Omit<
  PrepaymentUserBalanceBlockConfig,
  keyof PrepaymentUserBalanceBlockRuntimeConfig
> &
  Partial<PrepaymentUserBalanceBlockRuntimeConfig>;
export interface PrepaymentUserBalanceBlockContract extends ConfigComponentContract<
  PrepaymentUserBalanceBlockConfigInput,
  PrepaymentUserBalanceBlockResolvedConfig
> {}

@Component({
  selector: 'app-prepayment-user-balance-block',
  templateUrl: './prepayment-user-balance-block.component.html',
  standalone: true,
  imports: [CommonModule, TranslateModule, PriceAtomComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrepaymentUserBalanceBlockComponent implements PrepaymentUserBalanceBlockContract {
  readonly config = input<PrepaymentUserBalanceBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    PrepaymentUserBalanceBlockConfig,
    PrepaymentUserBalanceBlockRuntimeConfig
  >(prepaymentUserBalanceBlockResolveConfig, () => ({ config: this.config() }));
  private readonly prepaymentSelectorService = inject(PrepaymentSelectorService);
  private readonly prepaymentStoreDispatcher = inject(PrepaymentStoreDispatcher);

  public readonly headingColor = computed(
    () => this.resolvedConfig()['heading_color'] as unknown as IContentBuilderFieldColor,
  );

  public readonly shopEntity = computed(
    () => this.resolvedConfig()['shop_entity'] as unknown as ShopPickerEntities,
  );

  private readonly shopId = computed(() => {
    const shop = this.shopEntity()?.[0];
    return shop ? shop.id : '';
  });

  constructor() {
    effect(() => {
      if (!this.shopId()) {
        return;
      }
      this.prepaymentStoreDispatcher.needToDisplayUserBalance(this.shopId());
    });
  }

  protected readonly balanceBeforeOrderInCents = computed(() =>
    this.prepaymentSelectorService.selectBalanceBeforeOrderInCents(this.shopId())(),
  );

  protected readonly userBalanceLoaded = computed(() => {
    if (!this.shopId()) {
      return false;
    }
    return this.prepaymentSelectorService.selectUserBalanceLoaded(this.shopId())();
  });

  private readonly shopId$ = toObservable(this.shopId);
  // It seems that their is desync between signal and selector, so we use observable here
  protected readonly isLoading$ = this.shopId$.pipe(
    switchMap(shopId => this.prepaymentSelectorService.selectUserBalanceLoading$(shopId)),
  );
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(PrepaymentUserBalanceBlockComponent, {
  'keyAliases': {},
});
